import React, { useState, useMemo, useCallback } from 'react'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { layerManage } from '@/utils/layer'
import SearchInput from '@/components/search-input/search-input'
import { Column, Expaned, Row } from '@/components/layout/layout'
import { getBEMCls } from '@/utils/classname'
import './supplier.scss'
import Button from '@/components/button/button'
import Iconfont from '@/components/iconfont/iconfont'
import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { ISupplier } from '@/model/types/supllier'
import { $post } from '@/api'
import $msg from '@/utils/message'
import WordHighLight from '@/components/word-highlight/word-highlight'
import { goodsModel } from '../model'
import { useSelections, useMount, useRequest } from 'ahooks'
import { observer } from 'mobx-react'
import { errorCallBack } from '@/utils'

interface SupplierProps {
    selectSuplierIds: number[]
    onSelected: (data: ISupplier[]) => void
    selectType?: 'radio' | 'checkbox'
}

const Supplier: React.FC<SupplierProps> = observer(props => {
    const cls = (...args: string[]) => getBEMCls('supplier', ...args)
    const [suppliers, setSuppliers] = useState<ISupplier[]>([])
    const [keyWord, setKeyWord] = useState('')
    const [currentSupplier, setCurrentSupplier] = useState<ISupplier | null>(null)

    const { data: supplierData, loading } = useRequest<{
        suppliers: ISupplier[]
    }>(
        () =>
            $post({
                url: '/warehouse/getshopsuppliers',
                params: {
                    page: 1,
                    limit: 100,
                },
            }),
        {
            onSuccess: res => {
                setSuppliers(res.suppliers)
                if (props.selectType === 'radio' && props.selectSuplierIds.length) {
                    setCurrentSupplier(res.suppliers.filter(s => s.id === props.selectSuplierIds[0])[0])
                }
            },
            onError: errorCallBack,
        },
    )

    const allSupplierIds = useMemo(() => {
        if (!supplierData?.suppliers) {
            return []
        } else {
            return supplierData?.suppliers.map(s => s.id)
        }
    }, [supplierData])

    const supplierIdsSelection = useSelections<number>(
        allSupplierIds,
        props.selectSuplierIds?.length > 0 ? props.selectSuplierIds : [],
    )

    const getSelectIcon = useCallback(
        (record: ISupplier) => {
            if (props.selectType === 'checkbox') {
                return supplierIdsSelection.isSelected(record.id) ? 'icon3duoxuanerji-20' : 'icon3duoxuanguan-20'
            } else {
                return currentSupplier?.id === record.id ? 'icon3danxuankai-20' : 'icon3danxuanguan-20'
            }
        },
        [currentSupplier, props.selectType, supplierIdsSelection],
    )

    const columns: ColumnsType<ISupplier> = [
        {
            title: '',
            key: '1',
            width: '50px',
            render: (text, record, index) => (
                <div
                    data-cursor-pointer
                    onClick={() => {
                        if (props.selectType === 'checkbox') {
                            supplierIdsSelection.toggle(record.id)
                        } else {
                            if (currentSupplier?.id === record.id) return
                            setCurrentSupplier(record)
                        }
                    }}
                >
                    <Iconfont name={getSelectIcon(record)} size={20}></Iconfont>
                </div>
            ),
        },
        {
            title: '#',
            width: '50px',
            align: 'center',
            key: '2',
            render: (text, record, index) => {
                return <span>{index + 1}</span>
            },
        },
        {
            title: '供货商',
            key: 'name',
            dataIndex: 'name',
            render: text => <WordHighLight keyword={keyWord} word={text}></WordHighLight>,
        },
        {
            title: '联系电话',
            dataIndex: 'phone',
            render: (text, record, index) => <span>{text ? text : '暂无'}</span>,
        },
        {
            title: '采购次数',
            align: 'right',
            key: 'purchase_times',
            dataIndex: 'purchase_times',
            render: text => <span>{text + '次'}</span>,
        },
    ]

    useMount(() => {
        goodsModel.initSupplierList()
    })

    // 确认提交
    const confirmEvent = () => {
        if (props.selectType === 'checkbox') {
            const supliers = goodsModel.supplier.filter(item => supplierIdsSelection.selected.includes(item.id))
            props.onSelected(supliers)
        } else {
            props.onSelected(currentSupplier ? [currentSupplier] : [])
        }
        layerManage.pop()
    }

    const searchSupplier = (keyWord: string) => {
        if (keyWord.trim().length === 0) return
        $post({
            url: '/warehouse/getshopsuppliers',
            params: {
                search_keyword: keyWord,
                page: 1,
                limit: 100,
            },
        })
            .then(res => {
                setSuppliers(res.suppliers)
            })
            .catch(e => {
                $msg('error', e.message)
            })
    }

    const allSelectIcon = useMemo(() => {
        return supplierIdsSelection.allSelected
            ? 'icon3duoxuanquanxuan-20'
            : supplierIdsSelection.partiallySelected
            ? 'icon3duoxuanweiquanxuan-20'
            : 'icon3duoxuanguan-20'
    }, [supplierIdsSelection.allSelected, supplierIdsSelection.partiallySelected])

    return (
        <BaseDialog styles={{ width: '714px' }} isShow title="供货商列表" onClose={() => layerManage.pop()}>
            <Column data-padding-h-24 data-padding-top-16 data-padding-bottom-32>
                <SearchInput
                    placeholder="输入关键词搜索供货商"
                    onSearch={keyWord => {
                        setKeyWord(keyWord)
                        searchSupplier(keyWord)
                    }}
                    onDelete={() => setSuppliers(goodsModel.supplier)}
                ></SearchInput>
                <div className={cls('wrapper')}>
                    <Expaned>
                        <Table
                            columns={columns}
                            dataSource={suppliers}
                            pagination={false}
                            bordered
                            loading={loading}
                            scroll={{ y: 260, x: true }}
                            rowKey={record => record.id}
                        />
                    </Expaned>
                    <Row className={cls('footer')}>
                        <Row>
                            {props.selectType === 'checkbox' && (
                                <>
                                    <span data-padding-h-10 onClick={supplierIdsSelection.toggleAll}>
                                        <Iconfont name={allSelectIcon} size={20}></Iconfont>
                                    </span>
                                    <span data-font-size-14 data-color-000 data-row>
                                        已选
                                        <span data-padding-h-5 data-color-primary data-width-20 data-row>
                                            {supplierIdsSelection.selected.length}
                                        </span>
                                        供货商
                                    </span>
                                </>
                            )}
                        </Row>
                        <Button
                            types="default"
                            disabled={
                                props.selectType === 'checkbox'
                                    ? !supplierIdsSelection.selected.length
                                    : !currentSupplier
                            }
                            onClick={confirmEvent}
                        >
                            确认
                        </Button>
                    </Row>
                </div>
            </Column>
        </BaseDialog>
    )
})

Supplier.defaultProps = {
    selectType: 'checkbox',
}

export default Supplier
